import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  ExperimentStore,
  FetchOptions,
  FetchPageOptions,
  timeoutPromise
} from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import {
  AdditionalMeta,
  NamedMeta,
  PageData,
  PropertyMeta,
  ContentLanguageMeta,
  AlternateHrefLangLink
} from 'bernie-http';

import { getTrackingInfoForLogging } from '../common/logging-utils';
import { ActivityPageMetaTagType, ActivityPageSeoContent } from '../common/__generated__/api';
import { trackRecommendationModuleLoad } from '../helpers/activity-detail-store-helper';
import { handleInvalidDates } from '../helpers/controller-date-helper';
import { getGraphqlURL } from '../helpers/common-helper';

import { ActivityDetailStore, ActivitySeoContentStore, DetailCriteriaStore, ShortlistStore } from '../stores';
import { DetailQueryObject } from './../stores/detail-criteria-store';

const DISABLE_INDEXING_META = 'robots';
const MEXICO_SITE_ID = 12;
const MEXICO_REGION_ID = '117';
export class ActivityDetailController implements Controller {
  public pageId = 'page.LX.Infosite.Information,A,30';
  public path = '/:lang?/things-to-do/:title?.a:activityId.*';
  public routeName = 'activity-detail';
  public bundles: string[] = [];
  private loadReviews = true;

  // eslint-disable-next-line import/no-unresolved
  public component = codeSplit(() => import(/* webpackChunkName: "detail-page" */ 'views/activity-detail-page'));
  public exact = true;
  private clientSideFirstLoadRequested = true;

  public async fetch(options: FetchOptions): Promise<any> {
    const { stores, query, params } = options;
    const detailCriteriaStore: DetailCriteriaStore = stores.get<DetailCriteriaStore>('detailCriteriaStore');
    const activityDetailStore: ActivityDetailStore = stores.get<ActivityDetailStore>('activityDetailStore');
    const shortlistStore: ShortlistStore = stores.get<ShortlistStore>('shortlistStore');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const experimentStore: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const context: ContextStore = stores.get<ContextStore>('context');
    const config: KeyedObject = (context as any).config;

    detailCriteriaStore.setContext(context);
    detailCriteriaStore.localDateFormat = context.localDateFormat;
    shortlistStore.setContext(context);

    activityDetailStore.query = query;
    activityDetailStore.serverSide = options.isServer === true;
    activityDetailStore.setAnalyticsStore(analyticsStore);
    activityDetailStore.setExperimentStore(experimentStore);
    activityDetailStore.setPageLoading(true);

    const changedVariables = detailCriteriaStore.changedQueryKeys(query, params.activityId);

    if (changedVariables.indexOf('activityId') !== -1 || !activityDetailStore.activityDetail) {
      return timeoutPromise(
        this._getNewActivityDetailPage(
          activityDetailStore,
          detailCriteriaStore,
          shortlistStore,
          query,
          options,
          params,
          context,
          analyticsStore
        ),
        options.isServer ? config?.timeout?.serverControllerFetch : config?.timeout?.clientControllerFetch,
        `controller=detail fetch=getNewActivityDetailPage timeout ${getTrackingInfoForLogging(context)}`
      );
    } else if (changedVariables.indexOf('startDate') !== -1 || changedVariables.indexOf('endDate') !== -1) {
      return this._getChangeDateResponse(activityDetailStore, detailCriteriaStore, query, analyticsStore).then();
    }
    // Request these from the client side after server call

    return this._requestClientSideAfterDeeplink(activityDetailStore, detailCriteriaStore, analyticsStore);
  }

  private _getChangeDateResponse(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore,
    query: KeyedObject,
    analyticsStore: AnalyticsStore
  ): Promise<any> {
    detailCriteriaStore.detailQuery = query as DetailQueryObject;
    activityDetailStore.resetActivityOffers();

    return activityDetailStore
      .fetchActivityOffersWithAvailability(detailCriteriaStore.detailVariables, detailCriteriaStore, this.pageId, query)
      .then(() => {
        analyticsStore.resetAnalyticsForNewPage();
        this._publishAnalytics(activityDetailStore, analyticsStore);
      });
  }

  private _getNewActivityDetailPage(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore,
    shortlistStore: ShortlistStore,
    query: KeyedObject,
    options: FetchOptions,
    params: KeyedObject,
    context: ContextStore,
    analyticsStore: AnalyticsStore
  ): Promise<any> {
    detailCriteriaStore.resetDetailCriteriaStore();
    detailCriteriaStore.setContext(context);
    shortlistStore.setContext(context);

    let preloadedActivity;
    // Case of browser back from another infosite
    if (activityDetailStore.activityDetail) {
      preloadedActivity = {
        id: params.activityId,
        name: '',
        features: {},
        duration: {},
        leadTicket: {}
      };
    }
    activityDetailStore.resetDetailsPage(preloadedActivity);
    detailCriteriaStore.detailQuery = query as DetailQueryObject;
    detailCriteriaStore.setActivityId(params.activityId);

    detailCriteriaStore.detailQuery = handleInvalidDates(
      query,
      detailCriteriaStore.detailQuery,
      detailCriteriaStore.localDateFormat
    );

    return this._getNewActivityFetchPromise(
      activityDetailStore,
      context,
      detailCriteriaStore,
      shortlistStore,
      query,
      options
    ).then(() => {
      analyticsStore.resetAnalyticsForNewPage();
      this._publishAnalytics(activityDetailStore, analyticsStore);
      if (!options.isServer) {
        trackRecommendationModuleLoad(analyticsStore, activityDetailStore);
      }
    });
  }

  protected _fetchNewActivityServerPage(
    activityDetailStore: ActivityDetailStore,
    context: ContextStore,
    detailCriteriaStore: DetailCriteriaStore,
    shortlistStore: ShortlistStore,
    query: KeyedObject,
    options: FetchOptions
  ): Promise<any> {
    this.clientSideFirstLoadRequested = true;

    return Promise.all([
      activityDetailStore.fetchActivityDetail(
        detailCriteriaStore.detailVariables,
        detailCriteriaStore,
        this.pageId,
        query
      ),
      shortlistStore.fetchShortlist(options, this.pageId)
    ]);
  }

  private getPageData(regionId: string, activitySeoContentStore: ActivitySeoContentStore, siteId: number): PageData {
    if (activitySeoContentStore) {
      const title = activitySeoContentStore.title || '';
      const metaTags = activitySeoContentStore.metaTags;
      const hrefLangs = activitySeoContentStore.hreflangLinks;
      const seo = {
        robots: '',
        description: '',
        canonical: (activitySeoContentStore.canonicalUrl?.value as string) || '',
        additionalMeta: [] as AdditionalMeta[]
      };

      metaTags.forEach((tag: ActivityPageSeoContent.MetaTags) => {
        if (tag.content && tag.metaTagValue) {
          switch (tag.metaTagType) {
            case ActivityPageMetaTagType.NAME:
              if (tag.metaTagValue === 'description') {
                seo.description = tag.content;
              }
              seo.additionalMeta.push(new NamedMeta(tag.metaTagValue, tag.content));
              break;
            case ActivityPageMetaTagType.PROPERTY:
              seo.additionalMeta.push(new PropertyMeta(tag.metaTagValue, tag.content));
              break;
            default:
              seo.additionalMeta.push(new ContentLanguageMeta(tag.metaTagValue, tag.content));
              break;
          }
        }
      });

      if (siteId === MEXICO_SITE_ID && regionId === MEXICO_REGION_ID) {
        const disableIndexing = new NamedMeta(DISABLE_INDEXING_META, 'noindex, nofollow');
        seo.additionalMeta.push(disableIndexing);
      }

      hrefLangs.forEach(link => seo.additionalMeta.push(new AlternateHrefLangLink(link.hrefLang, link.href)));

      return { title, seo, pageId: this.pageId };
    }

    return { title: '', pageId: this.pageId };
  }

  protected _fetchNewActivityClientPage(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore,
    query: KeyedObject
  ): Promise<any> {
    this.clientSideFirstLoadRequested = false;

    return Promise.all([
      activityDetailStore
        .fetchActivityDetail(detailCriteriaStore.detailVariables, detailCriteriaStore, this.pageId, query)
        .then(() => {
          activityDetailStore.fetchActivityOffers(detailCriteriaStore.allOffersVariables, this.pageId);
          this._getReviewsPromise(activityDetailStore, detailCriteriaStore);
        }),
      activityDetailStore.fetchActivityRecommendations(detailCriteriaStore.recommendationVariables, this.pageId)
    ]);
  }

  private _getNewActivityFetchPromise(
    activityDetailStore: ActivityDetailStore,
    context: ContextStore,
    detailCriteriaStore: DetailCriteriaStore,
    shortlistStore: ShortlistStore,
    query: KeyedObject,
    options: FetchOptions
  ): Promise<any> {
    if (options.isServer) {
      return this._fetchNewActivityServerPage(
        activityDetailStore,
        context,
        detailCriteriaStore,
        shortlistStore,
        query,
        options
      );
    }

    return this._fetchNewActivityClientPage(activityDetailStore, detailCriteriaStore, query);
  }

  private _getOfferPromise(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore
  ): Promise<any> {
    if (detailCriteriaStore.selectedDate) {
      return activityDetailStore.fetchActivityOffers(detailCriteriaStore.allOffersVariables, this.pageId);
    }
    activityDetailStore.isOffersLoading = false;

    return Promise.resolve();
  }

  protected getReviewSeo(): Promise<any> {
    return Promise.resolve(import(/* webpackChunkName: "reviews-seo" */ '../reviews-seo'));
  }

  private _getReviewsPromise(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore
  ): Promise<any> {
    if (activityDetailStore.isReviewsPresent() && activityDetailStore.getReviewSource()) {
      detailCriteriaStore.setReviewSource(activityDetailStore.getReviewSource());
    }

    return this.getReviewSeo()
      .then(() => {
        this.loadReviews = true;
      })
      .catch(() => {
        this.loadReviews = !activityDetailStore.isExternalReview();
      })
      .finally(() => {
        activityDetailStore.isReviewsPresent() && this.loadReviews
          ? activityDetailStore.fetchActivityReviews(detailCriteriaStore.reviewsVariables, false, this.pageId)
          : Promise.resolve();
      });
  }

  private _requestClientSideAfterDeeplink(
    activityDetailStore: ActivityDetailStore,
    detailCriteriaStore: DetailCriteriaStore,
    analyticsStore: AnalyticsStore
  ): Promise<any> {
    if (this.clientSideFirstLoadRequested) {
      this.clientSideFirstLoadRequested = false;

      return activityDetailStore.serverSide
        ? Promise.resolve()
        : Promise.all([
            this._getOfferPromise(activityDetailStore, detailCriteriaStore),
            activityDetailStore
              .fetchActivityRecommendations(detailCriteriaStore.recommendationVariables, this.pageId)
              .then(() => {
                trackRecommendationModuleLoad(analyticsStore, activityDetailStore);
              }),
            this._getReviewsPromise(activityDetailStore, detailCriteriaStore)
          ]);
    }

    return Promise.resolve();
  }

  private _publishAnalytics(activityDetailStore: ActivityDetailStore, analyticsStore: AnalyticsStore): void {
    const hasAnalytics = activityDetailStore.uisPayload;
    if (hasAnalytics) {
      analyticsStore.publishClientSidePayload(activityDetailStore.uisPayload);
    }
  }

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const context: any = options.request.context as ContextStore;
    const config: KeyedObject = context.config;
    const seoContentStore = this.getActivitySeoContentStore(options, config, context);
    seoContentStore.setContext(options.request.context as any);
    const activityId = options.query.activityId;
    const regionId = options.query.regionId;

    return timeoutPromise(
      seoContentStore
        .fetchActivitySeoContent(activityId, this.pageId)
        .then(() => Promise.resolve(this.getPageData(regionId, seoContentStore, options.request.context.site.id)))
        .catch(() => Promise.resolve({ title: '', pageId: this.pageId })),
      config?.timeout?.serverControllerFetch,
      `controller=detail fetch=fetchActivitySeoContent timeout ${getTrackingInfoForLogging(context)}`
    ).catch(() => Promise.resolve({ title: '', pageId: this.pageId }));
  }

  protected getActivitySeoContentStore(
    options: FetchPageOptions,
    config: KeyedObject,
    context: any
  ): ActivitySeoContentStore {
    const logger = context.logger;
    const appName = config.appName;
    const bexApiUrl = getGraphqlURL(options.request.headers, config);
    const region = config.cdn.aws.region;

    return new ActivitySeoContentStore({ appName, bexApiUrl, region }, logger, config, options.request);
  }
}
